import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getVisitorToken } from '@/lib/visitor';

export interface Idea {
  id: string;
  title: string | null;
  body: string;
  author_name: string | null;
  vote_count: number;
  comment_count: number;
  created_at: string;
}

export type SortMode = 'new' | 'top';

export const useIdeas = (sort: SortMode) => {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());

  const fetchIdeas = useCallback(async () => {
    const column = sort === 'top' ? 'vote_count' : 'created_at';
    const { data } = await supabase
      .from('ideas')
      .select('id,title,body,author_name,vote_count,comment_count,created_at')
      .order(column, { ascending: false })
      .limit(50);
    setIdeas((data as Idea[]) ?? []);
    setLoading(false);
  }, [sort]);

  const fetchMyVotes = useCallback(async () => {
    const { data } = await supabase
      .from('idea_votes')
      .select('idea_id')
      .eq('voter_token', getVisitorToken());
    setVotedIds(new Set(((data as { idea_id: string }[]) ?? []).map(v => v.idea_id)));
  }, []);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  useEffect(() => {
    fetchMyVotes();
  }, [fetchMyVotes]);

  const vote = useCallback(
    async (ideaId: string) => {
      if (votedIds.has(ideaId)) return;
      setVotedIds(prev => new Set(prev).add(ideaId));
      setIdeas(prev => prev.map(i => (i.id === ideaId ? { ...i, vote_count: i.vote_count + 1 } : i)));
      const { error } = await supabase
        .from('idea_votes')
        .insert({ idea_id: ideaId, voter_token: getVisitorToken() });
      if (error) {
        setIdeas(prev => prev.map(i => (i.id === ideaId ? { ...i, vote_count: Math.max(0, i.vote_count - 1) } : i)));
      }
    },
    [votedIds],
  );

  return { ideas, loading, votedIds, vote, refetch: fetchIdeas };
};

export const postIdea = async (body: string, title: string | null, authorName: string | null) => {
  const { data, error } = await supabase
    .from('ideas')
    .insert({ body, title, author_name: authorName })
    .select('id,title,body,author_name,vote_count,comment_count,created_at')
    .single();
  if (error) throw error;
  return data as Idea;
};
